import clsx from "clsx";
import { useState, useReducer } from "react";
import {
  useActionData,
  useLoaderData,
  useParams,
  redirect,
  Form,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
} from "react-router";

import AssessmentApi from "@/api/assessment-api";
import CandidateApi from "@/api/candidate-api";
import CandidateInfo from "@/components/candidate-info";
import ApiErrorComponent from "@/components/common/api-error";
import Button from "@/components/common/input/button";
import Textarea from "@/components/common/input/textarea";
import type ApiError from "@/models/ApiError";
import type { AssessmentScoreInput } from "@/types/assessment";
import type { Candidate } from "@/types/candidate";
import type { Competency } from "@/types/competency";
import type { Scorecard } from "@/types/scorecard";

const errorboxclass = "mb-8 rounded-md border-2 border-red-500 bg-red-200 p-8 text-red-900";

// The page reads only the first entry of each list, and scoreCompetency writes
// an entry that carries a score and no user, so this is narrower than the
// Scorecard the loader puts in. A Scorecard still satisfies it.
interface AssessmentState {
  competencies: Competency[];
  scores: Record<string, { score: number }[]>;
  notes: Record<string, { note: string }[]>;
}

const empty_assessment: AssessmentState = { competencies: [], scores: {}, notes: {} };

type AssessmentAction = {
  type: "set-score";
  payload: Record<string, [{ score: number }]>;
};

const reducer = (
  state: { assessment: AssessmentState },
  assessment_action: AssessmentAction,
): { assessment: AssessmentState } => {
  switch (assessment_action.type) {
    case "set-score": {
      return {
        ...state,
        assessment: {
          ...state.assessment,
          scores: { ...state.assessment.scores, ...assessment_action.payload },
        },
      };
    }
  }
};

export default function AssessCandidate() {
  const { assessment, candidate, error } = useLoaderData() as LoaderData;
  const { assignment_id } = useParams();
  const submit_result = useActionData() as ActionData | undefined;
  // The reducer holds the assessment the page edits, so it needs a value even
  // on the failed load that the branch below renders instead.
  const [state, dispatch] = useReducer(reducer, { assessment: assessment ?? empty_assessment });

  if (error || !candidate) {
    return <ApiErrorComponent error={error ?? "error/candidate-not-loaded"} />;
  }

  function scoreCompetency(competency_id: string, score: number) {
    dispatch({ type: "set-score", payload: { [competency_id]: [{ score }] } });
  }

  // The action reports the competencies it refused to submit, by id.
  const unscored_competency_ids = submit_result?.unscored_competency_ids || [];
  const unscored_competency_names = state.assessment.competencies
    .filter((comp) => unscored_competency_ids.includes(comp.id))
    .map((comp) => comp.name);

  return (
    <div className="flex flex-col gap-6 pb-12">
      <CandidateInfo candidate={candidate} />
      {state.assessment.competencies.length === 0 && (
        <div className="mx-auto w-96 p-8 text-center">No competencies to assess.</div>
      )}
      <Form action={`/assignment/${assignment_id}/assess/${candidate.id}`} method="post">
        {state.assessment.competencies.map((comp) => {
          const [score_entry] = state.assessment.scores[comp.id] ?? [];
          const [note_entry] = state.assessment.notes[comp.id] ?? [];
          return (
            <div key={comp.id} className="mx-auto mb-8 w-[60rem]">
              <AssessCompetency
                competency={comp}
                score={score_entry && Number.isInteger(score_entry.score) ? score_entry.score : ""}
                note={note_entry?.note}
                scoreFn={scoreCompetency}
              />
            </div>
          );
        })}
        <div className="mx-auto w-[60rem]">
          {unscored_competency_names.length > 0 && (
            <div className={errorboxclass}>
              Add a score for {unscored_competency_names.join(", ")}.
              <br /> A note needs a score. It's ok to select <em>Did not assess</em>.
            </div>
          )}
          {submit_result?.error && (
            <div className={errorboxclass}>
              An error occured, please check all fields again.
              <br /> It's ok to select <em>Did not assess</em>.
            </div>
          )}
          {state.assessment.competencies.length > 0 && (
            <Button type="submit">Save assessment</Button>
          )}
        </div>
      </Form>
    </div>
  );
}

interface AssessCompetencyProps {
  competency: Competency;
  // The empty string is what the hidden field submits for an unscored one.
  score: number | "";
  note?: string | undefined;
  scoreFn: (competency_id: string, score: number) => void;
}

function AssessCompetency({ competency: comp, score, note, scoreFn }: AssessCompetencyProps) {
  const [is_open_indicators, setOpenIndicators] = useState(false);

  const middledivclass = clsx("flex items-center justify-between rounded-t-md bg-slate-800 p-4", {
    "rounded-b-md": !is_open_indicators,
  });

  function handleScore(
    ev: React.MouseEvent<HTMLButtonElement>,
    competency_id: string,
    new_score: number,
  ) {
    ev.preventDefault();
    scoreFn(competency_id, new_score);
  }

  return (
    <div key={comp.id}>
      <input type="hidden" name={`score:${comp.id}`} value={score} />
      <div className={middledivclass}>
        <div>
          <h2 className="mb-4 bg-slate-800 text-2xl font-bold">{comp.name}</h2>
          <p className="max-w-[40rem] text-slate-300">{comp.description}</p>
        </div>
        <button
          type="button"
          className="material-symbols-rounded cursor-pointer p-4 select-none hover:text-slate-600"
          onClick={() => setOpenIndicators(!is_open_indicators)}
          aria-expanded={is_open_indicators}
          aria-label={`${is_open_indicators ? "Hide" : "Show"} indicators for ${comp.name}`}
        >
          {is_open_indicators ? "expand_less" : "expand_more"}
        </button>
      </div>
      {is_open_indicators && (
        <>
          <div className="space-y-8 bg-slate-900 px-4 py-4">
            <p className="font-bold text-slate-100">Questions</p>
            {comp.questions_leader &&
              comp.questions_leader.map((question, idx) => {
                return (
                  <p key={idx} className="max-w-[40rem]">
                    {question}
                  </p>
                );
              })}
          </div>
          <div className="grid grid-cols-2">
            <div className="rounded-bl-md bg-green-200 px-4 pt-4 pb-2">
              <p className="pb-2 font-bold text-slate-600">Positive indicators</p>
              <ul className="ml-4 list-disc text-neutral-900">
                {comp.positive_indicators &&
                  comp.positive_indicators.map((indicator, idx) => {
                    return (
                      <li key={idx} className="mb-2">
                        {indicator}
                      </li>
                    );
                  })}
              </ul>
            </div>
            <div className="rounded-br-md bg-red-200 px-4 pt-4 pb-2">
              <p className="pb-2 font-bold text-slate-600">Negative indicators</p>
              <ul className="ml-4 list-disc text-neutral-900">
                {comp.negative_indicators &&
                  comp.negative_indicators.map((indicator, idx) => {
                    return (
                      <li key={idx} className="mb-2">
                        {indicator}
                      </li>
                    );
                  })}
              </ul>
            </div>
          </div>
        </>
      )}

      <div className="m-4 flex justify-center gap-4 rounded-b-md">
        <Button
          onClick={(ev) => handleScore(ev, comp.id, 1)}
          selected={score === 1 ? "accept" : null}
        >
          1
        </Button>
        <Button
          onClick={(ev) => handleScore(ev, comp.id, 2)}
          selected={score === 2 ? "accept" : null}
        >
          2
        </Button>
        <Button
          onClick={(ev) => handleScore(ev, comp.id, 3)}
          selected={score === 3 ? "accept" : null}
        >
          3
        </Button>
        <Button
          onClick={(ev) => handleScore(ev, comp.id, 4)}
          selected={score === 4 ? "accept" : null}
        >
          4
        </Button>
        <Button
          onClick={(ev) => handleScore(ev, comp.id, 5)}
          selected={score === 5 ? "accept" : null}
        >
          5
        </Button>
        <Button
          onClick={(ev) => handleScore(ev, comp.id, 0)}
          selected={score === 0 ? "default" : null}
        >
          Did not assess
        </Button>
      </div>
      <Textarea
        className="mx-auto min-h-[80px] max-w-[36rem]"
        defaultValue={note}
        placeholder="Add notes"
        name={`note:${comp.id}`}
      />
    </div>
  );
}

type LoaderData = {
  candidate: Candidate | null;
  assessment: Scorecard | null;
  error: ApiError | null;
};

export async function loader({
  request,
  params: { assignment_id, candidate_id },
}: LoaderFunctionArgs): Promise<LoaderData> {
  if (!candidate_id || !assignment_id) {
    throw new Error("Missing IDs");
  }
  const { data: assessment, error: assessment_error } = await AssessmentApi.getUserAssessment(
    candidate_id,
    request.signal,
  );
  const { data: candidate_data, error: candidate_error } = await CandidateApi.fetchOne(
    candidate_id,
    assignment_id,
    request.signal,
  );

  return {
    // The data is null exactly when the request failed, and the page renders
    // the error instead of the candidate.
    candidate: candidate_data ? candidate_data.candidate : null,
    assessment,
    error: assessment_error ? assessment_error : candidate_error ? candidate_error : null,
  };
}

// What the action returns to the page. A successful submission redirects, so
// neither key is set on the render that follows one.
type ActionData = {
  unscored_competency_ids?: string[];
  error?: ApiError;
};

export async function action({
  params,
  request,
}: ActionFunctionArgs): Promise<ActionData | Response> {
  const { candidate_id, assignment_id } = params;
  if (!candidate_id || !assignment_id) {
    throw new Error("Missing IDs");
  }
  const formdata = Object.fromEntries(await request.formData());
  const candidate_assessment: Record<string, Omit<AssessmentScoreInput, "competency_id">> = {};
  for (const formkey of Object.keys(formdata)) {
    const [category, competency_id] = formkey.split(":");
    // Every competency field is keyed "<category>:<competency_id>". Anything
    // without a colon is not one and stays out of the payload.
    if (competency_id === undefined) {
      continue;
    }
    // An untouched competency submits an empty score and an empty note, and it
    // stays out of the payload entirely. A score of zero arrives as "0". The
    // form posts hidden scores and note textareas only, so a non-string entry
    // is not part of an assessment either.
    const value = formdata[formkey];
    if (typeof value !== "string" || value === "") {
      continue;
    }
    if (category === "note") {
      candidate_assessment[competency_id] = {
        ...candidate_assessment[competency_id],
        note: value,
      };
    } else if (category === "score") {
      candidate_assessment[competency_id] = {
        ...candidate_assessment[competency_id],
        score: value,
      };
    }
  }
  const competencies: AssessmentScoreInput[] = [];
  for (const [competency_id, data] of Object.entries(candidate_assessment)) {
    competencies.push({ competency_id, ...data });
  }

  // The API stores a note on a score, so it rejects a note that has none, and
  // the rejection takes every other competency in the submission with it.
  const unscored = competencies.filter((competency) => competency.score === undefined);
  if (unscored.length > 0) {
    return {
      unscored_competency_ids: unscored.map((competency) => competency.competency_id),
    };
  }

  const { error } = await AssessmentApi.scoreCandidate(candidate_id, competencies);
  if (error) {
    return { error };
  }
  return redirect(`/assignment/${assignment_id}`);
}
